import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { BREAKPOINTS } from '@fragment/ui/src/constants';

export enum DeviceSize {
  Phone = 'phone',
  Tablet = 'tablet',
  WideTablet = 'wideTablet',
  Laptop = 'laptop',
  LargeLaptop = 'largeLaptop',
  Desktop = 'desktop',
}

type DeviceSizeContextType = {
  deviceSize: DeviceSize;
};

const DeviceSizeContext = createContext<DeviceSizeContextType | undefined>(
  undefined
);

const getDevice = (width: number): DeviceSize => {
  const { TABLET, WIDE_TABLET, LAPTOP, LARGE_LAPTOP, DESKTOP } = BREAKPOINTS;

  if (width < TABLET) {
    return DeviceSize.Phone;
  }
  if (width < WIDE_TABLET) {
    return DeviceSize.Tablet;
  }
  if (width < LAPTOP) {
    return DeviceSize.WideTablet;
  }
  if (width < LARGE_LAPTOP) {
    return DeviceSize.Laptop;
  }
  if (width < DESKTOP) {
    return DeviceSize.LargeLaptop;
  }
  return DeviceSize.Desktop;
};

export const getDeviceSize = (device: DeviceSize) => {
  const { TABLET, WIDE_TABLET, LAPTOP, LARGE_LAPTOP, DESKTOP } = BREAKPOINTS;
  switch (device) {
    case DeviceSize.Phone:
      return TABLET - 1; // phones are any size smaller than TABLET
    case DeviceSize.Tablet:
      return TABLET;
    case DeviceSize.WideTablet:
      return WIDE_TABLET;
    case DeviceSize.Laptop:
      return LAPTOP;
    case DeviceSize.LargeLaptop:
      return LARGE_LAPTOP;
    case DeviceSize.Desktop:
      return DESKTOP;
    default:
      return DESKTOP;
  }
};

export const DeviceSizeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [deviceSize, setDeviceSize] = useState<DeviceSize>(() =>
    // Initial size based on window width, defaulting to Desktop for SSR
    typeof window !== 'undefined'
      ? getDevice(window.innerWidth)
      : DeviceSize.Desktop
  );

  useEffect(() => {
    const handleResize = () => {
      setDeviceSize(getDevice(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const value = useMemo(() => ({ deviceSize }), [deviceSize]);

  return (
    <DeviceSizeContext.Provider value={value}>
      {children}
    </DeviceSizeContext.Provider>
  );
};

export const useDeviceSize = (): DeviceSize => {
  const context = useContext(DeviceSizeContext);
  if (!context) {
    throw new Error('useDeviceSize must be used within a DeviceSizeProvider');
  }
  return context.deviceSize;
};
